import { useState, useEffect } from 'react';
import { Plus, Building2, TrendingDown, Calculator, Search, ArrowDownToLine } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { StatCard } from '../../components/ui/StatCard';
import { Table } from '../../components/ui/Table';
import { FixedAssetFormModal } from '../../components/fixed-assets/FixedAssetFormModal';
import { GenerateDepreciationModal } from '../../components/fixed-assets/GenerateDepreciationModal';
import { FixedAssetDetailModal } from '../../components/fixed-assets/FixedAssetDetailModal';
import { DisposeFixedAssetModal } from '../../components/fixed-assets/DisposeFixedAssetModal';
import { useFixedAssets } from '../../hooks/useFixedAssets';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import { useCurrency } from '../../hooks/useCurrency';
import toast from 'react-hot-toast';
import type { FixedAsset } from '../../types/fixed-assets';
import {
  FIXED_ASSET_STATUS_LABELS,
  FIXED_ASSET_STATUS_COLORS,
  DEPRECIATION_METHOD_LABELS,
} from '../../types/fixed-assets';

export function FixedAssetsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDepreciationModal, setShowDepreciationModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDisposeModal, setShowDisposeModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<FixedAsset | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { currentFiscalYear } = useFiscalYearStore();
  const { organization } = useAuthStore();
  const { can } = usePermissions();
  const canManage = can('manage_fixed_assets');
  const { formatConverted } = useCurrency();

  const {
    assets,
    loading,
    error,
    loadAssets,
    createAsset,
    updateAsset,
    deleteAsset,
    generateDepreciation,
    disposeAsset,
    scrapAsset,
    activeAssets,
    totalNetBookValue,
    totalAcquisitionCost,
  } = useFixedAssets();

  useEffect(() => {
    loadAssets({ status: statusFilter || undefined, search: searchTerm || undefined });
    // `currentFiscalYear?.id` est ajouté explicitement aux dépendances : même
    // si `loadAssets` est mémoïsé sur `currentFiscalYear?.id` via useCallback,
    // certains React-DevTools/StrictMode optimisations peuvent stabiliser la
    // référence après le premier rendu. On force le rechargement quand l'utilisateur
    // change d'exercice dans la sidebar — sinon la liste reste figée sur
    // l'ancien FY = données stales affichées.
  }, [loadAssets, statusFilter, searchTerm, currentFiscalYear?.id]);

  const handleCreate = async (data: any) => {
    const result = await createAsset({
      ...data,
      fiscalYearId: currentFiscalYear?.id || '',
      companyId: organization?.id || '',
    });
    if (result) {
      toast.success('Immobilisation créée avec succès');
      setShowCreateModal(false);
    }
  };

  const handleUpdate = async (data: any) => {
    if (!selectedAsset) return;
    const result = await updateAsset(selectedAsset.id, data);
    if (result) {
      toast.success('Immobilisation mise à jour');
      setShowEditModal(false);
      setSelectedAsset(null);
    }
  };

  const handleDelete = async (asset: FixedAsset) => {
    if (!canManage) return;
    if (asset.status !== 'active') {
      toast.error('Seules les immobilisations actives peuvent être supprimées');
      return;
    }
    const success = await deleteAsset(asset.id);
    if (success) {
      toast.success('Immobilisation supprimée');
    }
  };

  const handleGenerateDepreciation = async (data: any) => {
    const result = await generateDepreciation(data);
    if (result) {
      toast.success(`${result.generatedCount} amortissement(s) généré(s) pour un total de ${formatConverted(result.totalAmount)}`);
      setShowDepreciationModal(false);
    }
  };

  const handleView = (asset: FixedAsset) => {
    setSelectedAsset(asset);
    setShowDetailModal(true);
  };

  const handleEdit = (asset: FixedAsset) => {
    setSelectedAsset(asset);
    setShowEditModal(true);
  };

  const handleOpenDispose = (asset: FixedAsset) => {
    setSelectedAsset(asset);
    setShowDisposeModal(true);
  };

  const depreciationRate = totalAcquisitionCost > 0
    ? Math.round(((totalAcquisitionCost - totalNetBookValue) / totalAcquisitionCost) * 100)
    : 0;

  const columns = [
    {
      header: 'Référence',
      accessor: (asset: FixedAsset) => (
        <div>
          <p className="font-medium text-foreground">{asset.name}</p>
          {asset.reference && (
            <p className="text-xs text-muted-foreground">{asset.reference}</p>
          )}
        </div>
      ),
    },
    {
      header: 'Date d\'acquisition',
      accessor: (asset: FixedAsset) => new Date(asset.acquisitionDate).toLocaleDateString('fr-FR'),
    },
    {
      header: 'Coût d\'acquisition',
      accessor: (asset: FixedAsset) => formatConverted(asset.acquisitionCost),
      className: 'text-right',
    },
    {
      header: 'Amort. cumulé',
      accessor: (asset: FixedAsset) => formatConverted(asset.cumulativeDepreciation),
      className: 'text-right',
    },
    {
      header: 'VNC',
      accessor: (asset: FixedAsset) => (
        <span className="font-semibold">{formatConverted(asset.netBookValue)}</span>
      ),
      className: 'text-right',
    },
    {
      header: 'Méthode',
      accessor: (asset: FixedAsset) => (
        <span className="text-xs">
          {DEPRECIATION_METHOD_LABELS[asset.depreciationMethod]} ({asset.usefulLifeYears} ans)
        </span>
      ),
    },
    {
      header: 'Statut',
      accessor: (asset: FixedAsset) => {
        const colors = FIXED_ASSET_STATUS_COLORS[asset.status];
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
            {FIXED_ASSET_STATUS_LABELS[asset.status]}
          </span>
        );
      },
    },
    {
      header: 'Actions',
      accessor: (asset: FixedAsset) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => handleView(asset)}>
            Voir
          </Button>
          {canManage && asset.status === 'active' && (
            <>
              <Button variant="ghost" size="sm" onClick={() => handleEdit(asset)}>
                Modifier
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={ArrowDownToLine}
                onClick={() => handleOpenDispose(asset)}
                title="Cession ou mise au rebut"
              >
                Céder
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Immobilisations</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Gestion des immobilisations et amortissements — SYSCOHADA
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <Button
                variant="outline"
                icon={Calculator}
                onClick={() => setShowDepreciationModal(true)}
                disabled={!currentFiscalYear || activeAssets.length === 0}
              >
                Générer amortissements
              </Button>
              <Button
                variant="success"
                icon={Plus}
                onClick={() => setShowCreateModal(true)}
                disabled={!currentFiscalYear || currentFiscalYear.status === 'closed'}
              >
                Nouvelle immobilisation
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Immobilisations actives"
          value={activeAssets.length}
          icon={Building2}
        />
        <StatCard
          title="Coût total d'acquisition"
          value={formatConverted(totalAcquisitionCost)}
          icon={Building2}
        />
        <StatCard
          title="VNC totale"
          value={formatConverted(totalNetBookValue)}
          icon={TrendingDown}
        />
        <StatCard
          title="Taux d'amortissement"
          value={`${depreciationRate}%`}
          icon={Calculator}
        />
      </div>

      {/* Filtres */}
      <Card>
        <div className="flex flex-wrap items-center gap-4 p-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Rechercher une immobilisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-select"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="disposed">Cédé</option>
            <option value="fully_depreciated">Totalement amorti</option>
          </select>
        </div>
      </Card>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Table */}
      <Card noPadding>
        <Table
          columns={columns}
          data={assets}
          loading={loading}
          emptyTitle="Aucune immobilisation"
          emptyMessage="Commencez par créer votre première immobilisation"
          onRowClick={handleView}
        />
      </Card>

      {/* Modales */}
      {showCreateModal && (
        <FixedAssetFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
        />
      )}

      {showEditModal && selectedAsset && (
        <FixedAssetFormModal
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setSelectedAsset(null); }}
          onSubmit={handleUpdate}
          asset={selectedAsset}
        />
      )}

      {showDepreciationModal && (
        <GenerateDepreciationModal
          isOpen={showDepreciationModal}
          onClose={() => setShowDepreciationModal(false)}
          onGenerate={handleGenerateDepreciation}
          fiscalYearId={currentFiscalYear?.id || ''}
        />
      )}

      {showDetailModal && selectedAsset && (
        <FixedAssetDetailModal
          isOpen={showDetailModal}
          onClose={() => { setShowDetailModal(false); setSelectedAsset(null); }}
          asset={selectedAsset}
          onEdit={canManage ? () => { setShowDetailModal(false); handleEdit(selectedAsset); } : undefined}
          onDelete={canManage ? () => { setShowDetailModal(false); handleDelete(selectedAsset); } : undefined}
        />
      )}

      {showDisposeModal && selectedAsset && (
        <DisposeFixedAssetModal
          isOpen={showDisposeModal}
          onClose={() => { setShowDisposeModal(false); setSelectedAsset(null); }}
          asset={selectedAsset}
          onDispose={async (payload) => {
            const result = await disposeAsset(selectedAsset.id, payload);
            if (result) {
              toast.success(`Cession comptabilisée — écriture ${result.journalEntryId.slice(0, 8)}`);
              setShowDisposeModal(false);
              setSelectedAsset(null);
            }
          }}
          onScrap={async (payload) => {
            const result = await scrapAsset(selectedAsset.id, payload);
            if (result) {
              toast.success(`Mise au rebut comptabilisée`);
              setShowDisposeModal(false);
              setSelectedAsset(null);
            }
          }}
        />
      )}
    </div>
  );
}
